// Public Terms of Use / Privacy Policy pages (/terms, /privacy) — required by
// the App Store and Google Play for subscription apps and linked from every
// paywall. Reachable without signing in.
//
// ⚠️ DRAFT TEXT — a plain-language starting point, NOT legal advice. Replace
// every [bracketed] placeholder and have it reviewed before launch.
import { useNavigate } from 'react-router-dom';

const UPDATED = 'November 2, 2026';

function Shell({ title, children }) {
  const navigate = useNavigate();
  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 20px 60px', lineHeight: 1.6, color: 'var(--ink, #111827)' }}>
      <a onClick={() => navigate('/')} style={{ cursor: 'pointer', fontSize: '0.875rem' }}>← Compassion Practice</a>
      <h1 style={{ marginBottom: 4 }}>{title}</h1>
      <div style={{ color: 'var(--muted, #6b7280)', fontSize: '0.875rem', marginBottom: 24 }}>Last updated {UPDATED}</div>
      {children}
    </div>
  );
}

export function TermsPage() {
  return (
    <Shell title="Terms of Use">
      <p>These terms govern your use of Compassion Practice (the website and the iOS and Android apps), operated by Medium Number Systems Inc. (“we”, “us”). By creating an account you agree to them.</p>

      <h3>1. Your account</h3>
      <p>You're responsible for your account and for keeping your sign-in details private. You must be at least 18 years old to use the service.</p>

      <h3>2. Subscription</h3>
      <ul>
        <li>Using the service requires a Premium subscription — <b>$4.99 per month</b> or <b>$49.99 per year</b>, whether you subscribe on the web, in the App Store or in Google Play (the app stores may show the local equivalent in your currency) — unless your role includes access.</li>
        <li><b>Free trial:</b> new accounts get one 7-day free trial. You'll be charged when the trial ends unless you cancel at least 24 hours before it ends. Each account gets one trial across the web, iOS and Android.</li>
        <li><b>Auto-renewal:</b> the subscription renews every month or year, depending on your plan, until you cancel. Cancelling stops future renewals; you keep access until the end of the period you've paid for.</li>
        <li><b>Where to manage it:</b> subscriptions bought on the web are managed on the Billing page (via Stripe). Subscriptions bought in the App Store or Google Play are managed in your Apple ID or Google Play account settings.</li>
        <li>Some courses, practices, events and groups have their own one-time price in addition to the subscription.</li>
        <li><b>Refunds</b> (purchases on the web):
          <ul>
            <li><b>Subscription:</b> cancel anytime; you keep access until the end of the period you've paid for. We don't give partial refunds for unused time.</li>
            <li><b>Courses and practices:</b> refundable within 7 days of purchase.</li>
            <li><b>Events:</b> refundable up to 7 days before the event; after that, at the event leader's discretion.</li>
            <li><b>Group fees:</b> refundable within 7 days of joining.</li>
            <li>To request a refund, email <a href="mailto:support@mediumnumber.com">support@mediumnumber.com</a>.</li>
          </ul>
          Purchases made through Apple or Google are refunded under their policies.</li>
      </ul>

      <h3>3. Community conversations</h3>
      <p><b>We have zero tolerance for objectionable content or abusive users.</b> When you post in a conversation you agree to the community guidelines: speak from your own experience, listen with empathy, and don't post harassment, hate, threats, sexual content, spam, or anyone's personal contact details. Keep what others share private.</p>
      <p>Posts are checked against a filter before they appear. You can report any post and block any person from the ⋯ menu. We review every report within 24 hours; content that breaks these rules is removed and the people who post it are removed from the service.</p>

      <h3>4. Your content</h3>
      <p>You own what you post. You give us permission to store and show it to the people it's shared with so the service can work.</p>

      <h3>5. Nature of the service</h3>
      <p>Compassion Practice supports learning and practising compassionate communication. It is not therapy, counselling or medical advice.</p>

      <h3>6. Ending your account</h3>
      <p>You can delete your account at any time from your profile. Deleting your account does not cancel an App Store or Google Play subscription — cancel it there too.</p>

      <h3>7. Changes and contact</h3>
      <p>We may update these terms and will say so in the app. Questions: <a href="mailto:support@mediumnumber.com">support@mediumnumber.com</a>.</p>
      <p>For purchases through the App Store, Apple's standard <a href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noreferrer">Licensed Application End User License Agreement</a> also applies.</p>
    </Shell>
  );
}

export function PrivacyPage() {
  return (
    <Shell title="Privacy Policy">
      <p>This policy explains what Medium Number Systems Inc. (“we”) collects when you use Compassion Practice and how it's used.</p>

      <h3>What we collect</h3>
      <ul>
        <li><b>Account details:</b> name, username, email, phone number (if you add it), and sign-in information handled by Google Firebase.</li>
        <li><b>What you do in the service:</b> courses, practices, events and groups you join; your notes and reflections; your posts, replies and reactions in conversations.</li>
        <li><b>Purchases:</b> subscription and purchase status. Card details are handled by Stripe, Apple or Google — we never see or store your full card number.</li>
        <li><b>Technical data:</b> basic logs and app analytics used to keep the service running and fix problems.</li>
      </ul>

      <h3>How we use it</h3>
      <p>To run the service, show your content to the people it's shared with, process payments, send emails you've chosen (confirmations, conversation digests, reminders), email members about new courses, practices, events and groups, keep the community safe, and improve the product. We don't sell your personal information.</p>

      <h3>Who we share it with</h3>
      <p>Service providers that help us run Compassion Practice: Google Cloud and Firebase (hosting, sign-in), Stripe, Apple, Google Play and RevenueCat (payments and subscriptions), Resend (email), and OpenAI (AI features). Event and group leaders see the name, email and phone of people who register so they can run the event.</p>

      <h3>Your choices</h3>
      <ul>
        <li>Change your email settings on any conversation page.</li>
        <li>Turn off emails about new courses, practices, events and groups with the unsubscribe link in any of them, or in Profile → Email preferences.</li>
        <li>Edit your profile at any time.</li>
        <li>Delete your account from your profile; this removes your personal data except where we must keep records by law (for example, payment records). Deleting stops a web subscription from renewing. So that the free trial can only be used once, we keep a one-way scrambled (hashed) form of your email address and the date your trial started — not your email itself.</li>
      </ul>

      <h3>Children</h3>
      <p>The service is for adults. It isn't intended for anyone under 18, and we don't knowingly collect information from anyone under 18.</p>

      <h3>Contact</h3>
      <p>Privacy questions or requests: <a href="mailto:privacy@compassionpractice.app">privacy@compassionpractice.app</a>. Compassion Practice is operated by Medium Number Systems Inc.</p>
    </Shell>
  );
}

// 2026-10-17 — public support page (App Store "Support URL"). Reachable
// without signing in.
export function SupportPage() {
  return (
    <Shell title="Compassion Practice Support">
      <p>We're happy to help. Email us at <a href="mailto:support@mediumnumber.com"><b>support@mediumnumber.com</b></a> and we'll reply within 2 business days. Please write from the email address on your account and tell us whether you use the web, iPhone or Android app.</p>

      <h3>Common questions</h3>

      <p><b>I forgot my password.</b><br />
      On the sign-in screen, choose <b>Forgot password?</b> and enter your email. We'll send you a link to set a new one.</p>

      <p><b>How do I manage or cancel my subscription?</b><br />
      Bought on the web: open <b>Billing</b> on compassionpractice.app. Bought on iPhone: Settings → your name → Subscriptions. Bought on Android: Google Play → Profile → Payments &amp; subscriptions → Subscriptions. Cancelling stops renewal; you keep access until the end of the period you've paid for.</p>

      <p><b>Can I get a refund?</b><br />
      Web purchases follow the refund rules in our <a href="/terms">Terms of Use</a> — email us to ask. Purchases made through Apple or Google are refunded by them under their policies.</p>

      <p><b>Does my subscription work on all my devices?</b><br />
      Yes. Sign in with the same account on the web, iPhone and Android.</p>

      <p><b>How do I report a post or block someone?</b><br />
      Tap the <b>⋯</b> menu on any post and choose Report or Block. We review every report within 24 hours.</p>

      <p><b>How do I delete my account?</b><br />
      Open <b>Profile</b> and choose <b>Delete account</b> at the bottom. See <a href="/delete-account">Delete your account</a> for details.</p>

      <p style={{ marginTop: 24 }}>See also our <a href="/terms">Terms of Use</a> and <a href="/privacy">Privacy Policy</a>. Compassion Practice is operated by Medium Number Systems Inc.</p>
    </Shell>
  );
}

// 2026-10-16 — public "how to delete your account" page for the Google Play
// Data safety form (must name the app, show the steps prominently, and say
// what is deleted / kept and for how long). Reachable without signing in.
export function DeleteAccountPage() {
  return (
    <Shell title="Delete your Compassion Practice account">
      <p>Compassion Practice (by Medium Number Systems Inc.) lets you delete your account and its data at any time.</p>

      <h3>How to delete your account</h3>
      <ol>
        <li><b>On the web:</b> sign in at <a href="https://compassionpractice.app">compassionpractice.app</a>, open <b>Profile</b>, and choose <b>Delete account</b> at the bottom of the page, then confirm.</li>
        <li><b>In the iPhone or Android app:</b> open the menu, go to <b>Profile</b>, and choose <b>Delete account</b> at the bottom, then confirm.</li>
        <li><b>By email:</b> if you can't sign in, email <a href="mailto:privacy@compassionpractice.app">privacy@compassionpractice.app</a> from the email address on your account and ask us to delete it. We delete it within 30 days and reply to confirm.</li>
      </ol>
      <p><b>Subscriptions:</b> deleting your account stops a subscription bought on the web from renewing. A subscription bought in the App Store or Google Play must be cancelled there (Apple ID or Google Play subscription settings) — deleting your account doesn't cancel it.</p>

      <h3>What is deleted</h3>
      <ul>
        <li>Your account and sign-in, and your profile (name, username, email, phone).</li>
        <li>Your posts and replies in conversations, your reflections and questions to instructors, and your progress.</li>
        <li>Your memberships of events and groups, and events you created.</li>
        <li>Deletion from the web or the app is immediate.</li>
      </ul>

      <h3>What is kept</h3>
      <ul>
        <li><b>Payment records</b> held by Stripe, Apple or Google, for as long as they and tax law require.</li>
        <li>A one-way scrambled (hashed) form of your email address and the date your free trial started, so the free trial can only be used once. It can't be turned back into your email address.</li>
        <li>Groups you lead stay open for their other members, without you as leader.</li>
      </ul>

      <p>Questions: <a href="mailto:privacy@compassionpractice.app">privacy@compassionpractice.app</a>. See also our <a href="/privacy">Privacy Policy</a>.</p>
    </Shell>
  );
}
